import { tr } from './tr.ts';
import { en } from './en.ts';

// Arayüz dili. İki dil var (Türkçe varsayılan, İngilizce). Seçim şu sırayla
// belirlenir: adres parametresi `?lang=en|tr` → bu tarayıcıda saklanan tercih →
// tarayıcı dili → Türkçe. Tercih yalnızca bu cihazda tutulur; sunucuya her
// API isteğinde `X-Lang` başlığıyla bildirilir ki hata mesajları ve paket
// metinleri aynı dilde gelsin.
//
// Sözlük anahtarları `tr.ts` içinde tanımlıdır; `en.ts` aynı anahtar kümesini
// TİP düzeyinde taşımak zorundadır (eksik çeviri derlemede yakalanır).

export type Lang = 'tr' | 'en';
export type MessageKey = keyof typeof tr;

const KEY = 'akis.lang';
const VALID: Lang[] = ['tr', 'en'];
const DICT: Record<Lang, Record<MessageKey, string>> = { tr, en };

let current: Lang = detect();
const listeners = new Set<(lang: Lang) => void>();

function detect(): Lang {
  try {
    const fromUrl = new URLSearchParams(location.search).get('lang');
    if (fromUrl && (VALID as string[]).includes(fromUrl)) {
      try { localStorage.setItem(KEY, fromUrl); } catch { /* depolama kapalı */ }
      return fromUrl as Lang;
    }
  } catch { /* location yok */ }
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && (VALID as string[]).includes(saved)) return saved as Lang;
  } catch { /* depolama kapalı */ }
  const nav = (navigator.language || '').toLowerCase();
  return nav.startsWith('tr') ? 'tr' : nav.startsWith('en') ? 'en' : 'tr';
}

export function lang(): Lang {
  return current;
}

/** Tarih ve sayı biçimlendirmesi için yerel ayar kodu. */
export function locale(): string {
  return current === 'en' ? 'en-GB' : 'tr-TR';
}

export function setLang(next: Lang): void {
  if (next === current) return;
  current = next;
  try { localStorage.setItem(KEY, next); } catch { /* depolama kapalı */ }
  applyDocumentLang();
  for (const listener of listeners) listener(next);
}

export function onLangChange(listener: (lang: Lang) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** `<html lang>` ve sayfa açıklaması seçili dile göre güncellenir. */
export function applyDocumentLang(): void {
  document.documentElement.lang = current;
  const description = document.querySelector('meta[name=description]');
  if (description) description.setAttribute('content', t('meta.description'));
  const ogDescription = document.querySelector('meta[property="og:description"]');
  if (ogDescription) ogDescription.setAttribute('content', t('meta.description'));
  const ogLocale = document.querySelector('meta[property="og:locale"]');
  if (ogLocale) ogLocale.setAttribute('content', current === 'en' ? 'en_GB' : 'tr_TR');
}

/**
 * Metin çevirisi. `{ad}` biçimindeki yer tutucular `params` ile doldurulur.
 * Anahtar sözlükte yoksa anahtarın kendisi döner: boş metin yerine sorunun
 * nerede olduğu ekranda görünür.
 */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  const text = DICT[current][key] ?? DICT.tr[key] ?? key;
  if (!params) return text;
  return text.replace(/\{(\w+)\}/g, (_, name: string) => (name in params ? String(params[name]) : `{${name}}`));
}

/** Seçili dile göre iki değerden birini verir; sözlüğe girmeyen tekil yerler için. */
export function pick<T>(trValue: T, enValue: T): T {
  return current === 'en' ? enValue : trValue;
}
